/**
 * Shared utility functions for routes feature
 *
 * These utilities handle address transformation, formatting, and
 * conversion between frontend and API data structures.
 */

import type { EnrichedRouteStop, RouteStopAddress } from './types';
import type { RouteStopResponse } from 'src/api/redux/routes';

/**
 * Entity address structure from useGetEntityDetailsByIdQuery
 */
export interface EntityAddress {
  address_key: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  state: string;
  zip_code: string;
  lat: number;
  lng: number;
  practice_name?: string;
  is_primary?: boolean;
}

/**
 * API stop payload for creating/updating route stops
 */
export interface ApiStopPayload {
  provider_id: string;
  position: number;
  address_key?: string;
}

/**
 * Format address parts into a display string
 * Filters out empty values and joins with comma separator
 */
export const formatAddressParts = (parts: {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}): string => {
  return [
    parts.addressLine1,
    parts.addressLine2,
    parts.city,
    parts.state,
    parts.zipCode,
  ]
    .filter(Boolean)
    .join(', ');
};

/**
 * Format an entity address (snake_case) into a display string
 */
export const formatEntityAddress = (address: EntityAddress): string => {
  return [
    address.address_line_1,
    address.address_line_2,
    address.city,
    address.state,
    address.zip_code,
  ]
    .filter(Boolean)
    .join(', ');
};

/**
 * Format an API route stop response into a display string
 */
export const formatApiStopAddress = (stop: RouteStopResponse): string => {
  return [
    stop.address_line_1,
    stop.address_line_2,
    stop.city,
    stop.state,
    stop.zip_code,
  ]
    .filter(Boolean)
    .join(', ');
};

/**
 * Convert an entity address to a RouteStopAddress
 */
export const entityAddressToRouteStopAddress = (
  address: EntityAddress,
  isPrimary = false
): RouteStopAddress => ({
  addressKey: address.address_key,
  addressLine1: address.address_line_1,
  addressLine2: address.address_line_2,
  city: address.city,
  state: address.state,
  zipCode: address.zip_code,
  lat: address.lat,
  lng: address.lng,
  practiceName: address.practice_name,
  isPrimary: address.is_primary ?? isPrimary,
});

/**
 * Transform API route stop responses to enriched frontend stops
 */
export const enrichRouteStops = (
  apiStops: RouteStopResponse[]
): EnrichedRouteStop[] => {
  return apiStops.map((stop) => ({
    id: String(stop.id),
    providerId: stop.provider_id,
    name: stop.name,
    address: formatApiStopAddress(stop),
    lat: stop.lat,
    lng: stop.lng,
    position: stop.position,
    addressKey: stop.address_key,
  }));
};

/**
 * Transform enriched frontend stops to API payload format
 * Filters out user location stops since they are not persisted
 */
export const toApiStopPayload = (
  stops: EnrichedRouteStop[]
): ApiStopPayload[] => {
  return stops
    .filter((stop) => !stop.isUserLocation)
    .map((stop, index) => ({
      provider_id: stop.providerId,
      position: index,
      address_key: stop.addressKey,
    }));
};

/**
 * Create an EnrichedRouteStop representing the user's current location.
 * Used as the route origin for optimization.
 */
export const createUserLocationStop = (location: {
  lat: number;
  lng: number;
}): EnrichedRouteStop => ({
  id: `user-location-${Date.now()}`,
  providerId: '',
  name: 'My Location',
  address: 'Current location',
  lat: location.lat,
  lng: location.lng,
  position: 0,
  isUserLocation: true,
});

/**
 * The points a route's directions run through: the starting point first when
 * there is one, then the stops, then the starting point again for a round trip.
 */
export const buildDirectionsPoints = (
  stops: RouteStopResponse[],
  startingPoint: { lat: number; lng: number } | null,
  roundTrip: boolean
): EnrichedRouteStop[] => {
  const enriched = enrichRouteStops(stops);
  const origin = startingPoint ? createUserLocationStop(startingPoint) : null;
  const points = origin
    ? [origin, ...enriched, ...(roundTrip ? [origin] : [])]
    : enriched;

  return points.map((point, index) => ({ ...point, position: index }));
};

// =============================================================================
// Mapbox Optimized Trips helpers
// =============================================================================

/**
 * Format stops as a Mapbox-compatible coordinate string.
 * Mapbox uses lng,lat order (opposite of Google).
 */
export const formatCoordinatesForMapbox = (
  stops: EnrichedRouteStop[]
): string => {
  return stops.map((stop) => `${stop.lng},${stop.lat}`).join(';');
};

/**
 * For one-way optimization with Mapbox (destination=last), the farthest
 * stop from the origin must be placed last. Mapbox treats the last coordinate
 * as the fixed destination.
 */
export const reorderFarthestLast = (
  stops: EnrichedRouteStop[],
  origin: { lat: number; lng: number }
): EnrichedRouteStop[] => {
  if (stops.length <= 1) return stops;

  let farthestIdx = 0;
  let maxDist = 0;

  for (let i = 0; i < stops.length; i++) {
    const dLat = stops[i].lat - origin.lat;
    const dLng = stops[i].lng - origin.lng;
    const dist = dLat * dLat + dLng * dLng;
    if (dist > maxDist) {
      maxDist = dist;
      farthestIdx = i;
    }
  }

  const reordered = [...stops];
  const [farthest] = reordered.splice(farthestIdx, 1);
  reordered.push(farthest);
  return reordered;
};

interface MapboxWaypoint {
  waypoint_index: number;
  name: string;
  location: [number, number];
}

interface MapboxOptimizedTripsResponse {
  code: string;
  waypoints?: MapboxWaypoint[];
}

/**
 * Call Mapbox Optimized Trips API to get optimal stop ordering.
 * Returns the stops reordered for the optimal route (origin first),
 * with updated position values, or null on failure.
 *
 * @see https://docs.mapbox.com/api/navigation/optimization/
 */
export const fetchMapboxOptimizedOrder = async (
  stops: EnrichedRouteStop[],
  options: { roundtrip?: boolean } = {}
): Promise<EnrichedRouteStop[] | null> => {
  const { roundtrip = false } = options;

  const accessToken = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN;
  if (!accessToken) {
    console.error('[fetchMapboxOptimizedOrder] No Mapbox access token configured');
    return null;
  }

  if (stops.length < 3) {
    console.warn('[fetchMapboxOptimizedOrder] Need at least 3 stops to optimize');
    return null;
  }

  // Sort stops by position
  const sortedStops = [...stops].sort((a, b) => a.position - b.position);

  const origin = sortedStops[0];
  const waypoints = sortedStops.slice(1);

  // For one-way mode, reorder so farthest waypoint is last (Mapbox requirement)
  const orderedWaypoints = roundtrip
    ? waypoints
    : reorderFarthestLast(waypoints, origin);

  const allStops = [origin, ...orderedWaypoints];
  const coordinates = formatCoordinatesForMapbox(allStops);

  const params = roundtrip
    ? 'source=first&roundtrip=true'
    : 'source=first&destination=last&roundtrip=false';

  try {
    const response = await fetch(
      `https://api.mapbox.com/optimized-trips/v1/mapbox/driving/${coordinates}?access_token=${accessToken}&${params}`
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        '[fetchMapboxOptimizedOrder] Mapbox API error:',
        `status=${response.status}`,
        errorText
      );
      return null;
    }

    const data: MapboxOptimizedTripsResponse = await response.json();

    if (data.code !== 'Ok' || !data.waypoints) {
      console.error('[fetchMapboxOptimizedOrder] Mapbox returned:', data.code);
      return null;
    }

    // Mapbox returns waypoint_index for each input coordinate indicating
    // its position in the optimized route. Pair each stop with its
    // optimized position, then sort to get the final order.
    const optimizedStops = allStops
      .map((stop, i) => ({
        stop,
        optimizedPosition: data.waypoints![i].waypoint_index,
      }))
      .sort((a, b) => a.optimizedPosition - b.optimizedPosition)
      .map(({ stop }, index) => ({
        ...stop,
        position: index,
      }));

    return optimizedStops;
  } catch (error) {
    console.error('[fetchMapboxOptimizedOrder] Error:', error);
    return null;
  }
};

// =============================================================================
// Route detail helpers
// =============================================================================

/** Mapbox Directions accepts at most 25 coordinates per request. */
export const DIRECTIONS_MAX_WAYPOINTS = 25;

/**
 * Split points into windows the directions API accepts. Windows overlap by one
 * point, so each starts where the previous one ended and the legs stay
 * contiguous when the results are stitched back together.
 */
export const chunkWaypoints = <T>(points: T[]): T[][] => {
  const windows: T[][] = [];
  for (
    let start = 0;
    start < points.length - 1;
    start += DIRECTIONS_MAX_WAYPOINTS - 1
  ) {
    windows.push(points.slice(start, start + DIRECTIONS_MAX_WAYPOINTS));
  }
  return windows;
};

/**
 * Identity of a stop for visit tracking. Editing a route replaces its stops
 * with new row ids, so visits are keyed by kind, provider and address instead.
 */
export const getStopKey = (stop: RouteStopResponse): string =>
  `${stop.provider_type}:${stop.provider_id}:${stop.address_key}`;

/** First stop (by position) that has not been visited, or undefined when all are. */
export const getNextUnvisitedStop = (
  stops: RouteStopResponse[],
  visitedStopKeys: string[]
): RouteStopResponse | undefined =>
  [...stops]
    .sort((a, b) => a.position - b.position)
    .find((stop) => !visitedStopKeys.includes(getStopKey(stop)));

/**
 * Reverse geocode a point to a place address with Mapbox. Returns null when no
 * address resolves or the request fails.
 */
export const reverseGeocodeLocation = async (location: {
  lat: number;
  lng: number;
}): Promise<string | null> => {
  const accessToken = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN;
  if (!accessToken) return null;

  try {
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${location.lng},${location.lat}.json?limit=1&access_token=${accessToken}`
    );
    if (!response.ok) return null;
    const data: { features?: Array<{ place_name?: string }> } =
      await response.json();
    return data.features?.[0]?.place_name ?? null;
  } catch {
    return null;
  }
};

// Backend Route.label is a CharField(max_length=255); reserve room for a
// " #NNNN" dedupe suffix so the base never shifts between runs.
const MAX_BASE_LABEL_LENGTH = 255 - 6;

/**
 * Build a unique route label, appending " #N" when the base (or a numbered
 * variant) already exists, e.g. "Jul 22" becomes "Jul 22 #2".
 */
export const buildRouteLabel = (
  baseLabel: string,
  existingLabels: string[]
): string => {
  const base = baseLabel.slice(0, MAX_BASE_LABEL_LENGTH);
  let maxNum = 0;
  for (const label of existingLabels) {
    if (label === base) {
      maxNum = Math.max(maxNum, 1);
    } else if (label.startsWith(`${base} #`)) {
      const num = parseInt(label.slice(base.length + 2), 10);
      if (Number.isFinite(num)) maxNum = Math.max(maxNum, num);
    }
  }
  return maxNum > 0 ? `${base} #${maxNum + 1}` : base;
};
